import appTables from '../assets/data/appTables.json';
import systemTables from '../assets/data/systemTables.json';


/**
 * Service that provides metadata about tables in the system
 * Used for synchronization, form generation, and data validation
 */
const tablesMetadataService = {
    /**
     * Get all table metadata from both app and system tables
     * @returns {Object} Combined table metadata
     */
    getTablesMetadata: () => {
        return {
            ...appTables,
            ...systemTables
        };
    },

    /**
     * Parse mapping string into structured array of id/text pairs
     * @param {string} mappingString - String of mappings in format "id=text;id=text"
     * @returns {Array} Array of mapping objects with id and text properties
     */
    parseMappings: (mappingString) => {
        if (!mappingString) return [];
        return mappingString.split(';')
          .map(pair => {
            const [id, text] = pair.split('=');
            try {
              return {
                id: parseInt(id.trim()),
                text: text.trim()
              };
            } catch (error) {
              console.error(`Error processing pair "${pair}":`, error);
              return null; // Return null for invalid entries
            }
          })
          .filter(item => item && !isNaN(item.id) && item.text);
    },

    /**
     * Get field definitions for a specific table
     * @param {string} tableName - Name of the table
     * @returns {Array|null} Array of field definitions or null if table not found
     */
    tableFields: (tableName) => {
        const tableDefinition = systemTables.tables[tableName] || appTables.tables[tableName];
        if (!tableDefinition) return null;

        // For Notification table, return only the essential fields in the simpler format
        if (tableName === 'Notification') {
            return [
                {
                    name: 'message',
                    displayName: 'Message',
                    type: 'STRING',
                    required: true
                },
                {
                    name: 'timestamp',
                    displayName: 'When',
                    type: 'DATE',
                    required: true
                },

                {
                    name: 'isRead',
                    displayName: 'Read',
                    type: 'BOOLEAN'
                }
            ];
        }

        // For other tables, transform from the full definition
        return tableDefinition.map(field => ({
            name: field.name,
            displayName: field.displayName,
            type: field.type,
            required: field.required === true || field.required === 1,
            mapping: field.mapping
        }));
    },

    /**
     * Get all syncable app tables
     * @returns {Array} Array of table names that are syncable
     */
    GetAllSyncableAppTables: () => {
        return Object.entries(appTables.tablesSummary)
            .filter(([_, config]) => config.Sync === 1)
            .map(([tableName]) => tableName);
    },

    /**
     * Get all syncable system tables
     * @returns {Array} Array of table names that are syncable
     */
    GetAllSyncableSystemTables: () => {
        return Object.entries(systemTables.tablesSummary)
            .filter(([_, config]) => config.Sync === 1)
            .map(([tableName]) => tableName);
    },

    /**
     * Get all syncable tables (both app and system)
     * @returns {Array} Array of all syncable table names
     */
    GetAllSyncableTables: () => {
        const appTableNames = tablesMetadataService.GetAllSyncableAppTables();
        const systemTableNames = tablesMetadataService.GetAllSyncableSystemTables();
        return [...appTableNames, ...systemTableNames];
    },

    /**
     * Check if a table requires UUID for synchronization
     * @param {string} tableName - Name of the table to check
     * @returns {boolean} True if the table needs UUID for sync, false otherwise
     */
    requiresUuidForSync: (tableName) => {
        const appTable = appTables.tablesSummary[tableName];
        const systemTable = systemTables.tablesSummary[tableName];
        
        return (appTable && appTable.Sync === 1) || 
               (systemTable && systemTable.Sync === 1);
    },

    /**
     * Get all tables that have dependencies on other tables
     * @returns {Object} Map of table names to their dependency information
     */
    getTableDependencies: () => {
        const dependencies = {};
        
        // Add Circle-Invitation dependency
        dependencies.Invitation = {
            dependsOn: ['Circle'],
            uuidField: 'CircleUuid',
            idField: 'circleId'
        };
        
        // Add other dependencies here as needed
        
        return dependencies;
    }
};

export default tablesMetadataService;